'use client';

import React, { useRef, useEffect, useMemo, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from 'src/components/PulseUI/Typography';
import { CriteriaComponentType } from 'src/redux/slices/aiListGeneratorSlice';
import useUnifiedCriteriaSearch, {
  UnifiedSearchResult,
  GroupedSearchResults,
  LoadingStates,
} from '../../hooks/useUnifiedCriteriaSearch';
import { MIN_SEARCH_LENGTH } from '../../constants';
import { SECTION_ORDER, getTypeConfig } from './config';

interface Position {
  x: number;
  y: number;
}

interface SlashCommandMenuProps {
  isOpen: boolean;
  position: Position;
  searchTerm: string; // Search term comes from parent (typed after /)
  onSelect: (result: UnifiedSearchResult) => void;
  onClose: () => void;
  allowedTypes?: CriteriaComponentType[];
}

/**
 * Flatten grouped results into a single array for keyboard navigation
 */
const flattenResults = (
  results: GroupedSearchResults,
  sectionOrder: CriteriaComponentType[]
): UnifiedSearchResult[] => {
  const flattened: UnifiedSearchResult[] = [];
  sectionOrder.forEach((type) => {
    const key = type as keyof GroupedSearchResults;
    if (results[key]) {
      flattened.push(...results[key]);
    }
  });
  return flattened;
};

const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  isOpen,
  position,
  searchTerm, // Now received from parent
  onSelect,
  onClose,
  allowedTypes,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [menuDimensions, setMenuDimensions] = useState({
    width: 320,
    height: 400,
  });

  const { results, isLoading, loadingStates, hasResults } =
    useUnifiedCriteriaSearch(searchTerm, {
      allowedTypes,
      maxResultsPerType: 5,
    });

  // Filter section order to only show allowed types
  const filteredSectionOrder = useMemo(() => {
    if (!allowedTypes) return SECTION_ORDER;
    return SECTION_ORDER.filter((type) => allowedTypes.includes(type));
  }, [allowedTypes]);

  const isSearchable = (searchTerm?.length ?? 0) >= MIN_SEARCH_LENGTH;

  // Flatten results for keyboard navigation (only the results on screen)
  const flattenedResults = useMemo(
    () => (isSearchable ? flattenResults(results, filteredSectionOrder) : []),
    [isSearchable, results, filteredSectionOrder]
  );

  // Reset selected index when menu opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Update menu dimensions
  useEffect(() => {
    if (isOpen && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      setMenuDimensions({
        width: rect.width,
        height: rect.height,
      });
    }
  }, [isOpen, hasResults]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flattenedResults.length]);

  // Handle selection
  const handleSelect = useCallback(
    (result: UnifiedSearchResult) => {
      onSelect(result);
      setSelectedIndex(0);
    },
    [onSelect]
  );

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) =>
            prev < flattenedResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          e.stopPropagation();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Tab':
          e.preventDefault();
          e.stopPropagation();
          // Tab selects the first result instantly
          if (flattenedResults.length > 0) {
            handleSelect(flattenedResults[0]);
          }
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          if (flattenedResults[selectedIndex]) {
            handleSelect(flattenedResults[selectedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, selectedIndex, flattenedResults, onClose, handleSelect]);

  // Calculate adjusted position to keep menu in viewport
  const adjustedPosition = useMemo(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = menuDimensions.width;
    const menuHeight = menuDimensions.height;

    let x = position.x;
    let y = position.y;

    // Keep within horizontal bounds
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 8;
    }
    x = Math.max(8, x);

    // Flip above if not enough space below
    if (y + menuHeight > viewportHeight) {
      y = position.y - menuHeight - 12;
    }
    y = Math.max(8, y);

    return { x, y };
  }, [position, menuDimensions]);

  // Scroll selected item into view
  useEffect(() => {
    if (!isOpen) return;
    const selectedElement = menuRef.current?.querySelector(
      `[data-index="${selectedIndex}"]`
    );
    if (selectedElement) {
      selectedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, isOpen]);

  if (!isOpen) return null;

  // Calculate global index for each item
  let globalIndex = 0;

  return createPortal(
    <>
      {/* Backdrop - transparent click handler */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        data-testid="slash-menu-backdrop"
      />

      {/* Menu - appears directly below the slash command */}
      <div
        ref={menuRef}
        data-testid="slash-menu"
        className="fixed z-50 bg-white border border-neutral-200 rounded-lg shadow-lg w-80 max-h-80 flex flex-col overflow-hidden"
        style={{
          top: adjustedPosition.y,
          left: adjustedPosition.x,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Results */}
        <div className="flex-1 overflow-y-auto">
          {/* Empty state - search term too short to search yet */}
          {!isSearchable && (
            <div className="p-4 text-center" data-testid="slash-menu-hint">
              <Typography className="text-sm text-neutral-500">
                Type to search providers, codes, payers...
              </Typography>
            </div>
          )}

          {/* Loading state - debouncing or requests in flight, nothing to show yet */}
          {isSearchable && isLoading && !hasResults && (
            <div className="p-4 text-center" data-testid="slash-menu-loading">
              <FontAwesomeIcon
                icon={faSpinner}
                className="animate-spin text-neutral-400 mr-2"
              />
              <Typography className="text-sm text-neutral-500 inline">
                Searching...
              </Typography>
            </div>
          )}

          {/* No results */}
          {isSearchable && !isLoading && !hasResults && (
            <div className="p-4 text-center" data-testid="slash-menu-no-results">
              <Typography className="text-sm text-neutral-500">
                No results found for &ldquo;{searchTerm}&rdquo;
              </Typography>
            </div>
          )}

          {/* Grouped Results */}
          {isSearchable &&
            hasResults &&
            filteredSectionOrder.map((type) => {
              const key = type as keyof GroupedSearchResults;
              const sectionResults = results[key] ?? [];
              const config = getTypeConfig(type);
              const isLoadingSection =
                loadingStates[key as keyof LoadingStates];

              if (sectionResults.length === 0 && !isLoadingSection) {
                return null;
              }

              return (
                <div key={type} className="border-b border-neutral-100 last:border-b-0">
                  {/* Section Header */}
                  <div className="sticky top-0 bg-neutral-50 px-3 py-1.5 flex items-center justify-between backdrop-blur-sm">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={config.icon}
                        className={`w-3 h-3 ${config.textColorClass}`}
                      />
                      <Typography className="text-xs font-medium text-neutral-600">
                        {config.labelPlural}
                      </Typography>
                      {sectionResults.length > 0 && (
                        <span className="text-xs text-neutral-400">
                          ({sectionResults.length})
                        </span>
                      )}
                    </div>
                    {isLoadingSection && (
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin text-neutral-400 w-3 h-3"
                      />
                    )}
                  </div>

                  {/* Section Results */}
                  {sectionResults.map((result) => {
                    const currentIndex = globalIndex++;
                    const isSelected = currentIndex === selectedIndex;
                    const typeConfig = getTypeConfig(result.criteriaType);

                    return (
                      <button
                        key={`${result.criteriaType}-${result.id}`}
                        type="button"
                        data-index={currentIndex}
                        data-testid={`slash-menu-result-${result.criteriaType}-${result.id}`}
                        onClick={() => handleSelect(result)}
                        className={`w-full px-3 py-2 text-left transition-colors flex items-start gap-2 ${
                          isSelected
                            ? `${typeConfig.bgColorClass} border-r-2 ${typeConfig.borderColorClass}`
                            : 'hover:bg-neutral-50'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm truncate ${
                              isSelected
                                ? `${typeConfig.textColorClass} font-medium`
                                : 'text-neutral-900'
                            }`}
                          >
                            {result.displayLabel}
                          </p>
                          {result.secondaryLabel && (
                            <p
                              className={`text-xs truncate ${
                                isSelected
                                  ? typeConfig.textColorClass
                                  : 'text-neutral-500'
                              }`}
                            >
                              {result.secondaryLabel}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
        </div>

        {/* Footer hint */}
        {isSearchable && hasResults && (
          <div className="px-3 py-2 border-t border-neutral-100 bg-neutral-50">
            <Typography className="text-xs text-neutral-500">
              <span className="font-medium">Tab</span> to select first •{' '}
              <span className="font-medium">↑↓</span> navigate •{' '}
              <span className="font-medium">Enter</span> select •{' '}
              <span className="font-medium">Esc</span> close
            </Typography>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default SlashCommandMenu;
